import { zodResolver } from '@hookform/resolvers/zod';
import {
  ISettingsConfigTOCPermissionSchema,
  QuoteAndContractSettingsConfig,
  SettingsConfigTOCPermissionSchema,
} from '@monetize/types/experience-config';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  MBox,
  MButton,
  MFlex,
  MRadio,
  MRadioGroup,
  MText,
} from '../../../../../../components/Monetize';
import { UserRoleMultiSelect } from '../../../../../../components/Monetize/Dropdowns/UserRoleMultiSelect';
import { SettingsControl } from '../../../../components/SettingsControl';
import { QuoteAndContractSettingsMetadata } from './quote-settings-experience-config.utils';

const TermsAndConditionsOptions: {
  key: 'true' | 'false' | 'SPECIFIC_ROLES';
  displayLabel: string;
}[] = [
  { key: 'false', displayLabel: 'Always visible' },
  { key: 'true', displayLabel: 'Never visible' },
  { key: 'SPECIFIC_ROLES', displayLabel: 'Visible to specific roles' },
];

interface QuoteTermsAndConditionsProps {
  value: ISettingsConfigTOCPermissionSchema;
  onSave: (value: ISettingsConfigTOCPermissionSchema) => void;
}

export const QuoteTermsAndConditions = ({
  value: externalValue,
  onSave,
}: QuoteTermsAndConditionsProps) => {
  const {
    control,
    reset,
    handleSubmit,
    watch,
    formState: { isDirty },
  } = useForm({
    mode: 'onChange',
    resolver: zodResolver(SettingsConfigTOCPermissionSchema),
    defaultValues: externalValue,
  });

  useEffect(() => {
    reset(externalValue);
  }, [reset, externalValue]);

  const disableQuoteTermsAndConditions = watch(
    'disableQuoteTermsAndConditions',
  );

  function handleSave(
    data: QuoteAndContractSettingsConfig['showTOCPermission'],
  ) {
    onSave(data);
  }

  return (
    <>
      <Controller
        name={`disableQuoteTermsAndConditions`}
        control={control}
        render={({ field: { value, onChange, ...rest } }) => (
          <SettingsControl
            heading={
              QuoteAndContractSettingsMetadata['showTOCPermission'].label
            }
            subheading={
              QuoteAndContractSettingsMetadata['showTOCPermission'].description
            }
            control={
              <MRadioGroup
                display="flex"
                flexDir="column"
                gap={2}
                minWidth="200px"
                value={String(value)}
                onChange={(selected: string) => {
                  onChange(
                    selected === 'true' || selected === 'false'
                      ? selected === 'true'
                      : selected,
                  );
                }}
              >
                {TermsAndConditionsOptions.map(({ key, displayLabel }) => (
                  <MRadio value={String(key)} key={String(key)} mt="1">
                    {displayLabel}
                  </MRadio>
                ))}
              </MRadioGroup>
            }
            {...rest}
          />
        )}
      />
      {disableQuoteTermsAndConditions === 'SPECIFIC_ROLES' && (
        <Controller
          name={`allowedRoles`}
          control={control}
          render={({ field: { value, ...rest } }) => (
            <MBox pb={4} mt={4}>
              <MFlex
                flexDirection="row"
                justifyContent="space-between"
                mt={4}
                alignItems="center"
              >
                <MText minWidth="100px">Allowed Roles</MText>
                <UserRoleMultiSelect value={value} {...rest} />
              </MFlex>
            </MBox>
          )}
        />
      )}
      {isDirty && (
        <MFlex justifyContent="flex-end">
          <MButton
            type="button"
            data-testid="save-quote-settings-showTOCPermission"
            variant="primary"
            mt={4}
            alignSelf="self-end"
            onClick={() => handleSubmit(handleSave)()}
          >
            Save
          </MButton>
        </MFlex>
      )}
    </>
  );
};
